import { Injectable, inject } from '@angular/core';
import { InitEventDisruptionApiService } from 'ngx-mt-ui-lib/api-client';
import { SecurityEventsApiService } from '@core/api/security-events-api.service';
import { ReactingProtocolApiService } from 'ngx-mt-ui-lib/api-client';
import type { IGridRequestBody } from 'ngx-mt-ui-lib/interfaces';
import type {
  ChatEntityReferenceSearchRequest,
  ChatReferenceSuggestion,
  ChatReferenceSuggestionSource,
  ChatEntityReferenceTrigger,
} from 'ias-ppr-mt-chat-client';
import { map, of } from 'rxjs';
import type { Observable } from 'rxjs';

@Injectable()
export class AscupChatReferenceSuggestionSource implements ChatReferenceSuggestionSource {
  public readonly triggers: readonly ChatEntityReferenceTrigger[] = [
    { kind: 'nnr', prefix: 'ннр-' },
    { kind: 'sb', prefix: 'сб-' },
    { kind: 'protocol-template', prefix: 'пр-', allowSpaces: true },
  ];
  private readonly disruptions = inject(InitEventDisruptionApiService);
  private readonly securityEvents = inject(SecurityEventsApiService);
  private readonly protocols = inject(ReactingProtocolApiService);

  public search(
    request: ChatEntityReferenceSearchRequest,
  ): Observable<readonly ChatReferenceSuggestion[]> {
    const gridRequest = createGridRequest(request);

    if (request.kind === 'protocol-template') {
      return this.protocols.grid({ ...gridRequest, sortings: [] }).pipe(
        map((response) =>
          response.items.map((item) => ({
            kind: 'entity' as const,
            target: { type: 'ias-ppr:protocol-template', id: item.id },
            label: item.templateVersionName,
            description: item.templateNumber || null,
          })),
        ),
      );
    }
    if (request.kind !== 'nnr' && request.kind !== 'sb') return of([]);

    return request.kind === 'nnr'
      ? this.disruptions.getGrid(gridRequest).pipe(
          map((response) =>
            response.items.map((item) => ({
              kind: 'entity' as const,
              target: { type: 'ias-ppr:NNR', id: item.id },
              label: item.initEventNumber,
              description: describeEvent(item.stationName, item.description),
            })),
          ),
        )
      : this.securityEvents.getGrid(gridRequest).pipe(
          map((response) =>
            response.items.map((item) => ({
              kind: 'entity' as const,
              target: { type: 'ias-ppr:SB', id: item.id },
              label: item.initEventNumber,
              description: describeEvent(item.stationName, item.description),
            })),
          ),
        );
  }
}

function createGridRequest(
  request: ChatEntityReferenceSearchRequest,
): IGridRequestBody {
  return {
    sortings: [{ field: 'id', direction: 'desc', index: 0 }],
    complexFilters: [],
    page: { pageNumber: 1, pageSize: request.limit },
    searchString: request.query.trim() || null,
    disableCount: true,
  };
}

function describeEvent(
  stationName: string,
  description: string,
): string | null {
  const parts = [stationName, description]
    .map((value) => value?.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
